import * as React from 'react'
import { FormControl, FormHelperText, CircularProgress, TextField, MenuItem } from '@mui/material'
import { ContactUsRequest } from 'typescript-fetch-api'
import { withGoogleReCaptcha, IWithGoogleReCaptchaProps } from 'react-google-recaptcha-v3'

import * as Container from '../containers/ContactUsForm'
import { isValidEmail } from '../../../common/auth/functions'
import { getSortedData, isInvalidString } from '../../../common/util/functions'
import { validateNumberInput } from '../../../utils/functions'
import Button from '../../platform/components/Button'
import { Branch } from '../../../common/showrooms/types'

import '../styles/ContactUsForm.css'

interface State {
	mobileNumber: string
	email: string
	firstName: string
	lastName: string
	selectedAccount: string
	selectedBranch: string
	notes: string
	mobileNumberError?: string
	emailError?: string
	firstNameError?: string
	lastNameError?: string
	selectedAccountError?: string
	selectedBranchError?: string
	notesError?: string
	mobileOrEmailError?: string
	hasOngoingSubmission: boolean
}

const INITIAL_STATE: State = {
	mobileNumber: '',
	email: '',
	firstName: '',
	lastName: '',
	notes: '',
	selectedAccount: '',
	selectedBranch: '',
	hasOngoingSubmission: false,
}

class ContactUsForm extends React.PureComponent<Container.Props & Container.Actions & Partial<IWithGoogleReCaptchaProps>, State> {

	state = INITIAL_STATE

	componentDidMount() {
		this.props.clearForm()
		this._initialiseFields()
	}

	componentDidUpdate(prevProps: Container.Props) {
		const { selectedAccount, sentContactUs, sendingContactUsError } = this.props

		// the selected account has changed, update the dropdown value
		if (prevProps.selectedAccount && selectedAccount && prevProps.selectedAccount.id !== selectedAccount.id) {
			this.setState({ selectedAccount: selectedAccount!.customerId.toString() })
		}

		// contact request has been successfully sent, clear form
		if (prevProps.sentContactUs !== sentContactUs && sentContactUs) {
			if (selectedAccount) {
				// we have an account selected, only clear the message and the flag for checking an ongoing submission
				this.setState({ notes: '', hasOngoingSubmission: false })
			} else {
				this.setState(INITIAL_STATE)
			}
		}

		// contact request failed, clear flag for ongoing submission to remove loader
		if (prevProps.sendingContactUsError !== sendingContactUsError && sendingContactUsError) {
			this.setState({ hasOngoingSubmission: false })
		}

		if (prevProps.defaultBranchId && this.props.defaultBranchId && prevProps.defaultBranchId !== this.props.defaultBranchId) {
			this.setState({ selectedBranch: this.props.defaultBranchId })
		}
	}

	_initialiseFields = () => {
		const { selectedAccount, defaultBranchId } = this.props

		if (selectedAccount) {
			let stateValues: Partial<State> = { selectedAccount: selectedAccount.customerId.toString() }

			if (selectedAccount.firstName) {
				stateValues = { ...stateValues, firstName: selectedAccount.firstName }
			}
			if (selectedAccount.lastName) {
				stateValues = { ...stateValues, lastName: selectedAccount.lastName }
			}
			if (selectedAccount.mobileNumber) {
				stateValues = { ...stateValues, mobileNumber: selectedAccount.mobileNumber }
			}
			if (selectedAccount.email) {
				stateValues = { ...stateValues, email: selectedAccount.email }
			}
			if (defaultBranchId) {
				stateValues = { ...stateValues, selectedBranch: defaultBranchId.toString() }
			}

			this.setState(stateValues as State)
		}
	}

	_onSubmit = () => {
		// it takes a while for the recaptcha request to go through, so we use this flag to know if a submission is ongoing.
		// this prevents the user performing multiple clicks on the submit button, which in turn fires off multiple similar requests.
		this.setState({ hasOngoingSubmission: true })
		// validate the form
		this._onValidate()
	}

	_onValidate = async () => {
		const { loggedIn, accounts, googleReCaptchaProps, branches } = this.props
		const { mobileNumber, email, firstName, lastName, selectedAccount, selectedBranch, notes } = this.state
		const hasAccounts: boolean = accounts.length !== 0

		if (!firstName) {
			this.setState({ firstNameError: 'Enter a first name', hasOngoingSubmission: false })
			return
		} else {
			if (isInvalidString(firstName)) {
				this.setState({ firstNameError: 'Please enter a valid first name', hasOngoingSubmission: false })
				return
			}
		}
		if (!lastName) {
			this.setState({ lastNameError: 'Enter a last name', hasOngoingSubmission: false })
			return
		} else {
			if (isInvalidString(lastName)) {
				this.setState({ lastNameError: 'Please enter a valid last name', hasOngoingSubmission: false })
				return
			}
		}
		if (!mobileNumber && !email) {
			this.setState({ mobileOrEmailError: 'A mobile number or email is required', hasOngoingSubmission: false })
			return
		} else if (email && !isValidEmail(email)) {
			this.setState({ emailError: 'Please enter a valid email', hasOngoingSubmission: false })
			return
		}
		// only check for the presence of a selected account if the user is logged in and they have accounts
		if (loggedIn && hasAccounts && !selectedAccount) {
			this.setState({ selectedAccountError: 'A selected account is required', hasOngoingSubmission: false })
			return
		}
		if (!selectedBranch) {
			this.setState({ selectedBranchError: 'A selected branch is required', hasOngoingSubmission: false })
			return
		}
		if (!notes) {
			this.setState({ notesError: 'Enter your inquiry', hasOngoingSubmission: false })
			return
		} else {
			if (isInvalidString(notes)) {
				this.setState({ notesError: 'Please enter a valid inquiry', hasOngoingSubmission: false })
				return
			}
		}

		let reCaptchaToken = ''
		// we check if recaptcha is ready so we can execute it and grab the token
		if (googleReCaptchaProps && googleReCaptchaProps.executeRecaptcha) {
			reCaptchaToken = await googleReCaptchaProps.executeRecaptcha('contact_us')
		}

		const branch: Branch | undefined = branches.find(item => item.id === selectedBranch)
		let contactUsParams: ContactUsRequest = {
			firstname: firstName,
			lastname: lastName,
			email,
			phone: mobileNumber,
			branch: branch ? `${branch.id} - ${branch.name}` : selectedBranch,
			message: notes,
		}
		if (loggedIn && hasAccounts) {
			const account = accounts.find(item => item.customerId.toString() === selectedAccount)

			// the user is logged in so we know they should have an account selected
			contactUsParams = {
				...contactUsParams,
				accountId: selectedAccount,
				accountName: (account && account.name) || '',
			}
		}

		this.props.onSubmitContactUsForm(reCaptchaToken, contactUsParams)
	}

	/**
	 * INPUT CHANGES
	 */

	_handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { value, id } = e.target
		this.setState({ [id]: value, [`${id}Error`]: undefined, mobileOrEmailError: undefined } as Omit<State, 'selectedAccount' | 'selectedAccountError' | 'selectedBranch' | 'selectedBranchError' | 'hasOngoingSubmission'>)
	}

	_handleMobileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { value } = e.target
		const isValidInput: boolean = validateNumberInput(value)
		if (isValidInput) {
			this.setState({ mobileNumber: value, mobileNumberError: undefined, mobileOrEmailError: undefined })
		}
	}

	_handleNotesInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
		const notes: string = e.target.value
		this.setState({ notes, notesError: undefined })
	}

	_handleSelectOnChange = (event: React.ChangeEvent<{ name?: string; value: unknown }>) => {
		const { name, value } = event.target
		this.setState({ [`${name}`]: value, [`${name}Error`]: undefined } as Pick<State, 'selectedAccount' | 'selectedAccountError' | 'selectedBranch' | 'selectedBranchError'>)
	}

	/**
	 * SELECT/DROPDOWN HELPERS
	 */

	_getAccountOptions = (): { value: string, label: string }[] => {
		const { accounts } = this.props
		if (accounts.length === 0) {
			return []
		}
		const accountOptions = accounts.map(account => ({
			value: account.customerId.toString(),
			label: `${account.customerId} - ${account.name}`
		}))
		return getSortedData(accountOptions, 'label')
	}

	render() {
		const { sendingContactUs, sendingContactUsError, sentContactUs, loggedIn, accounts, branches } = this.props
		const {
			mobileNumber, email, firstName, lastName, selectedAccount, selectedBranch, notes, hasOngoingSubmission,
			mobileNumberError, emailError, firstNameError, lastNameError, selectedAccountError, selectedBranchError, notesError, mobileOrEmailError,
		} = this.state
		const hasAccounts: boolean = accounts.length !== 0
		const isSendingContactUs = sendingContactUs || hasOngoingSubmission

		return (
			<div className="contact-us-form">
				<div className="contact-us-form__content">
					<FormControl>
						<TextField
							type="text"
							id="firstName"
							label="First Name*"
							value={firstName}
							onChange={this._handleInputChange}
							error={!firstName && !!firstNameError}
							variant="outlined"
							autoComplete="off"
						/>
						<FormHelperText error={!firstName && !!firstNameError}>{firstNameError}</FormHelperText>
					</FormControl>

					<FormControl>
						<TextField
							type="text"
							id="lastName"
							label="Last Name*"
							value={lastName}
							onChange={this._handleInputChange}
							error={!lastName && !!lastNameError}
							variant="outlined"
							autoComplete="off"
						/>
						<FormHelperText error={!lastName && !!lastNameError}>{lastNameError}</FormHelperText>
					</FormControl>

					<FormControl>
						<TextField
							type="email"
							id="email"
							label="Email"
							value={email}
							onChange={this._handleInputChange}
							error={!email && !!emailError}
							variant="outlined"
							autoComplete="off"
						/>
						<FormHelperText error={!email && !!emailError}>{emailError}</FormHelperText>
					</FormControl>

					<FormControl>
						<TextField
							type="text"
							id="mobileNumber"
							label="Mobile"
							value={mobileNumber}
							onChange={this._handleMobileInputChange}
							error={!mobileNumber && !!mobileNumberError}
							variant="outlined"
							autoComplete="off"
						/>
						<FormHelperText error={!mobileNumber && !!mobileNumberError}>{mobileNumberError}</FormHelperText>
					</FormControl>

					{loggedIn && hasAccounts && (
						// only display the selected account dropdown if the user is logged in and they have accounts
						<FormControl>
							<TextField
								name="selectedAccount"
								value={selectedAccount}
								onChange={this._handleSelectOnChange}
								label="Account*"
								SelectProps={{
									MenuProps: {
										anchorOrigin: {
											vertical: 'bottom',
											horizontal: 'left'
										},
										transformOrigin: {
											vertical: 'top',
											horizontal: 'left'
										},
									}
								}}
								variant="outlined"
								select={true}
								autoComplete="off"
							>
								{this._getAccountOptions().map(option => (
									<MenuItem
										key={option.label}
										value={option.value}
									>
										{option.label}
									</MenuItem>
								))}
							</TextField>
							<FormHelperText error={!!selectedAccountError}>{selectedAccountError}</FormHelperText>
						</FormControl>
					)}

					<FormControl>
						<TextField
							name="selectedBranch"
							value={selectedBranch}
							onChange={this._handleSelectOnChange}
							label="Branch*"
							SelectProps={{
								MenuProps: {
									anchorOrigin: {
										vertical: 'bottom',
										horizontal: 'left'
									},
									transformOrigin: {
										vertical: 'top',
										horizontal: 'left'
									},
								}
							}}
							variant="outlined"
							select={true}
							autoComplete="off"
						>
							{branches.map(branch => (
								<MenuItem
									key={branch.id}
									value={branch.id}
								>
									{branch.name}
								</MenuItem>
							))}
						</TextField>
						<FormHelperText error={!!selectedBranchError}>{selectedBranchError}</FormHelperText>
					</FormControl>
				</div>

				<FormControl>
					<TextField
						id="notes"
						className="contact-us-form__text-area"
						value={notes}
						onChange={this._handleNotesInputChange}
						placeholder="Enter message here..."
						variant="outlined"
						multiline={true}
						rows={5}
						error={!!notesError}
						InputLabelProps={{ shrink: false }}
						autoComplete="off"
					/>
					<FormHelperText error={!!notesError}>{notesError}</FormHelperText>
				</FormControl>

				<div className="contact-us-form__actions">
					{!isSendingContactUs && <Button className="contact-us-form__button" onClick={this._onSubmit} kind="teal">SUBMIT</Button>}

					{/*
					* We are hiding the recaptcha badge so we must add these links to the page
					* https://developers.google.com/recaptcha/docs/faq#id-like-to-hide-the-recaptcha-badge.-what-is-allowed
					*/}
					<div className="contact-us-form__text--recaptcha">
						This site is protected by reCAPTCHA and the Google <br />
						<a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer"> Privacy Policy</a> and
						<a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer"> Terms of Service</a> apply.
					</div>

					{isSendingContactUs && <CircularProgress className="contact-us-form__progress" />}
					{sentContactUs && <span className="contact-us-form__text message success">Your message has been sent. Thank you for getting in contact with us.</span>}
					{sendingContactUsError && <span className="contact-us-form__text message error">Unable to send message. Check your network and try again.</span>}
					{mobileOrEmailError && <div className="contact-us-form__text message error">{mobileOrEmailError}</div>}
				</div>
			</div>
		)
	}
}

export default withGoogleReCaptcha(ContactUsForm)